import { Cancel, CheckCircle, Description, Help } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import {
  AI_MODEL_EVENTS,
  AI_MODELS,
  AI_MODELS_STATES,
  AIOSYN_KIDNEY_AI_MODELS,
  AIRA_LIVER_AI_MODELS,
  MODEL_VALID_ORGANS,
  MODEL_VALID_SPECIES,
  MODEL_VALID_STAIN_TYPES,
  QC_AI_MODELS,
  TISSUE_AI_MODELS,
} from 'components/AITools/constants/common';
import { STAIN_TYPE_VALUES } from 'constants/common';
import {
  getClearHex,
  LIGHT_GREY_3,
  LINK_COLOR,
  rgbaToHex,
} from 'utilities/colors';
import { MAP_AI_MODEL_TO_DISPLAY_COLOR } from 'components/AITools/constants/styles';
import XLSX from 'xlsx-js-style';

export const calculateModelArea = (coordinates) => {
  function polygonArea(coords) {
    let area = 0;
    const n = coords.length / 2; // number of vertices

    for (let i = 0; i < n; i += 1) {
      const x1 = coords[2 * i];
      const y1 = coords[2 * i + 1];
      const x2 = coords[2 * ((i + 1) % n)];
      const y2 = coords[2 * ((i + 1) % n) + 1];
      area += x1 * y2 - x2 * y1;
    }

    return Math.abs(area) / 2;
  }

  return coordinates.reduce(
    (total, polyObj) => total + polygonArea(polyObj.polygon),
    0,
  );
};

export const getShortModelName = (modelName) =>
  modelName.replace(/^ml_/, '').replace(/_data$/, '');

export const calculateAreasPercentage = (area, absoluteArea) => {
  if (!area || !absoluteArea || area === absoluteArea) return null;

  const percentage = (area / absoluteArea) * 100;

  return percentage.toFixed(4);
};

export const getFilledPolygonsOnly = (coordinates) =>
  Array.isArray(coordinates)
    ? coordinates.filter((item) => item.type === 'fill' && item.polygon)
    : [];

export const handleRegularModel = (_coordinates, tissueModelCoordinates) => {
  if (!_coordinates || !Array.isArray(_coordinates)) return null;

  const metadata = _coordinates[0];

  if (!metadata.count) {
    metadata.count = getFilledPolygonsOnly(_coordinates).length.toString();
  }

  if (!metadata.area_percentage) {
    const tissueArea = tissueModelCoordinates?.[0]?.area;
    const modelArea = calculateModelArea(getFilledPolygonsOnly(_coordinates));
    metadata.area = modelArea;
    metadata.area_percentage = calculateAreasPercentage(modelArea, tissueArea);
  }

  return _coordinates;
};

export const handleTissueModel = (_coordinates) => {
  if (!_coordinates || !Array.isArray(_coordinates)) return null;

  const metadata = _coordinates[0];
  metadata.area = calculateModelArea(getFilledPolygonsOnly(_coordinates));

  if (!metadata.count) {
    metadata.count = getFilledPolygonsOnly(_coordinates).length;
  }

  return _coordinates;
};

export const extractQuantificationsFromModel = (aiModels, fullModelName) => {
  const shortModelname = getShortModelName(fullModelName);
  const modelCoordinates = aiModels[shortModelname];
  const metadata = modelCoordinates?.[0];
  const { area_percentage, count } = metadata ?? {};

  return area_percentage || count
    ? {
      percentage: area_percentage || null,
      count: count || null,
    }
    : null;
};

export const isSlideTissueMLDataAvailable = (slide, model) => {
  const attribute = `${model}_state`;

  return slide[attribute] === AI_MODELS_STATES.EXISTS;
};

export const isQCModelExists = (slide, modelName) =>
  !!slide?.additional_data?.[modelName]?.regions;

export const isQCModel = (modelName) =>
  Object.values(QC_AI_MODELS).includes(modelName);

export const isTissueModel = (modelName) =>
  Object.values(TISSUE_AI_MODELS).includes(modelName);

export const isAiraModel = (modelName) =>
  Object.values(AIRA_LIVER_AI_MODELS).includes(modelName);

export const isAiosynModel = (modelName) =>
  Object.values(AIOSYN_KIDNEY_AI_MODELS).includes(modelName);

export const raiseOpacityChangeEvent = ({
  airaOpacity,
  aiosynOpacity,
  tissueOpacity,
  qcOpacity,
  segmentationOpacity,
}) => {
  document.dispatchEvent(
    new CustomEvent(AI_MODEL_EVENTS.OPACITY_CHANGE, {
      detail: {
        airaOpacity,
        aiosynOpacity,
        tissueOpacity,
        qcOpacity,
        segmentationOpacity,
      },
    }),
  );
};

export const getAnalysisType = (modelId) => {
  switch (modelId) {
    case AI_MODELS.aira:
      return 'aira_liver';
    case AI_MODELS.aiosyn:
      return 'aiosyn_kidney';
    case AI_MODELS.tissueSegmentation:
      return 'tissue_segmentation';
    case AI_MODELS.autoQC:
      return 'quality_control';
    case AI_MODELS.semanticSegmentation:
      return 'histomorpho';
    default:
      return '';
  }
};

export const getMLModel = (modelId) => {
  switch (modelId) {
    case 'aira_liver':
      return AI_MODELS.aira;
    case 'aiosyn_kidney':
      return AI_MODELS.aiosyn;
    case 'tissue_segmentation':
      return AI_MODELS.tissueSegmentation;
    case 'quality_control':
      return AI_MODELS.autoQC;
    case 'histomorpho':
      return AI_MODELS.semanticSegmentation;
    default:
      return '';
  }
};

export const filterComputedSlides = (data, state, model) => {
  const filterData = model
    ? data.filter((log) => log.processing_type === getAnalysisType(model))
    : data;

  const slides =
    filterData
      ?.filter((log) => log.state === state)
      .map((log) => log.slides)
      .flat() || [];

  const uniqueSlidesMap = slides.reduce((a, c) => {
    a.set(c.uuid, c);

    return a;
  }, new Map());

  return [...uniqueSlidesMap.values()];
};

export const isModelExists = (slide, model) => {
  if (model.id === AI_MODELS.autoQC) {
    return isQCModelExists(slide, model.options[0].value);
  }

  const modelState = `${model?.options?.[0]?.value}_state`;

  return slide[modelState] === AI_MODELS_STATES.EXISTS;
};

export const isCompatibleDataMissing = (slide, model) => {
  const specificOrgans = MODEL_VALID_ORGANS[model.id];
  const isOrganMissing = specificOrgans?.length && !slide?.sample?.organ?.name;

  const specificSpecies = MODEL_VALID_SPECIES[model.id];
  const isSpeciesMissing =
    specificSpecies?.length && !slide?.sample?.species?.name;

  const specificStainTypes = MODEL_VALID_STAIN_TYPES[model.id];
  const isStainTypeMissing =
    specificStainTypes && !STAIN_TYPE_VALUES[slide?.stain_type];

  return isOrganMissing || isSpeciesMissing || isStainTypeMissing;
};

export const isStainTypeMismatch = (slide, model) => {
  if (!slide.stain_type) return false;

  const specificStainTypes = MODEL_VALID_STAIN_TYPES[model.id];

  return specificStainTypes?.length
    ? !specificStainTypes.includes(STAIN_TYPE_VALUES[slide?.stain_type])
    : false;
};

export const isOrganMismatch = (slide, model) => {
  if (!slide?.sample?.species?.name) return false;

  const specificOrgans = MODEL_VALID_ORGANS[model.id];

  return specificOrgans?.length
    ? !specificOrgans.includes(slide?.sample?.organ?.name)
    : false;
};

export const isSpeciesMismatch = (slide, model) => {
  if (!slide?.sample?.species?.name) return false;

  const specificSpecies = MODEL_VALID_SPECIES[model.id];

  return specificSpecies?.length
    ? !specificSpecies.includes(slide?.sample?.species?.name)
    : false;
};

export const isCompatibleSlide = (slide, model) =>
  !isStainTypeMismatch(slide, model) &&
  !isSpeciesMismatch(slide, model) &&
  !isOrganMismatch(slide, model);

export const getIssuesCount = (slides = [], model) =>
  slides.reduce((acc, slide) => {
    const emptyFieldsCount = isCompatibleDataMissing(slide, model);
    const organError = isOrganMismatch(slide, model);
    const speciesError = isSpeciesMismatch(slide, model);
    const stainError = isStainTypeMismatch(slide, model);

    if (stainError) return acc;

    return acc + +(emptyFieldsCount || organError || speciesError);
  }, 0);

export const getModalCompatibilityIcon = (slide, model) => {
  const isCompatible = isCompatibleSlide(slide, model);
  const isDataMissing = !!isCompatibleDataMissing(slide, model);

  switch (true) {
    case isDataMissing:
      return (
        <Tooltip
          title="Species / Organ / Stain Type missing. This may indicate that the metadata for this tool is missing, incomplete, or incorrect. Edit metadata or proceed with caution."
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '40rem',
              },
            },
          }}
        >
          <Help sx={{ color: '#888888' }} fontSize="small" />
        </Tooltip>
      );
    case !isCompatible:
      return (
        <Tooltip
          title="Species / Organ / Stain Type Mismatch. This tool is likely not compatible with this slide and will not run. If you are absolutely certain the metadata is incorrect, you must correct it before proceeding."
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '40rem',
              },
            },
          }}
        >
          <Cancel sx={{ color: '#D40505' }} fontSize="small" />
        </Tooltip>
      );
    case isModelExists(slide, model):
      return <Description sx={{ color: LINK_COLOR }} fontSize="small" />;
    default:
      return <CheckCircle color="success" fontSize="small" />;
  }
};

export const serializeAiModelOptions = ({ model, aiModels, opacityOptions }) =>
  [...model.options].map((option) => ({
    ...option,
    color:
      model.id !== AI_MODELS.autoQC &&
      MAP_AI_MODEL_TO_DISPLAY_COLOR[option.value],
    quantification: extractQuantificationsFromModel(aiModels, option.value),
    isDisabled: opacityOptions.disabled(option.value),
  }));

export const getExportAiModelOptions = ({
  slides,
  model,
  aiModels,
  opacityOptions,
}) =>
  slides.map((slide) => ({
    slideName: slide.name,
    options: serializeAiModelOptions({
      model,
      aiModels: aiModels[slide.uuid] || {},
      opacityOptions,
    }),
  }));

export const exportToExcel = (aiModelData) => {
  const worksheetData = [];
  const slideName = aiModelData.length > 1 ? '' : aiModelData[0].slideName;
  let nameColumnWidth = 4;

  aiModelData.forEach(({ slideName: slideNameValue, options }) => {
    worksheetData.push([slideNameValue]);
    options.forEach((option) => {
      nameColumnWidth =
        option.name.length > nameColumnWidth
          ? option.name.length
          : nameColumnWidth;
      worksheetData.push([
        '',
        {
          v: '',
          s: {
            fill: {
              fgColor: {
                rgb: option.isDisabled
                  ? getClearHex(LIGHT_GREY_3)
                  : rgbaToHex(option.color),
              },
            },
          },
        },
        option.name,
        option.quantification?.count || '',
      ]);
    });
  });

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  const fileName = `${
    slideName || `${aiModelData.length}slides`
  }-quantification.xlsx`;

  worksheet['!cols'] = [{ wch: 4 }, { wch: 4 }, { wch: nameColumnWidth }];

  worksheetData.forEach((worksheetRow, index) => {
    if (worksheetRow.length === 1) {
      worksheet['!merges'] = [{ s: { r: index, c: 0 }, e: { r: index, c: 3 } }];
    }
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, fileName);
};

export const getShortenedSlidesPageUrl = () => {
  const url = window.location.href;
  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex !== -1 ? url.slice(0, hashIndex) : url;

  return beforeHash;
};
